import React, { useCallback, useEffect, useRef, useState } from 'react';
import AuthService from '../../services/AuthService';
import LeadStatusService from '../../services/LeadStatusService';
import ClientService from '../../../SuperAdmin/services/ClientService';
import { apiErrorMessage } from '../../../../shared/utils/apiErrorMessage';
import { getStatusDisplayName } from '../../constants/leadConstants';

const PRESET_COLORS = [
  '#5e72e4', '#11cdef', '#2dce89', '#fb6340', '#f5365c',
  '#ffd600', '#8965e0', '#f3a4b5', '#adb5bd', '#344767',
];

const defaultForm = { leadStatusName: '', color: '#5e72e4', sortOrder: 0 };

const pageBg = { padding: '16px 12px 32px', minHeight: '100vh', background: '#f4f6fb', width: '100%', boxSizing: 'border-box' };
const card = {
  background: '#fff',
  borderRadius: 14,
  padding: '16px 18px',
  boxShadow: '0 2px 12px rgba(52,71,103,0.07)',
  border: '1px solid rgba(233,236,239,0.8)',
};
const inp = {
  width: '100%',
  padding: '6px 8px',
  borderRadius: 8,
  border: '1px solid #e9ecef',
  fontSize: 12,
  color: '#344767',
  background: '#fff',
  boxSizing: 'border-box',
};
const th = { padding: '10px 12px', fontSize: 11, fontWeight: 700, color: '#8898aa', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' };
const td = { padding: '8px 12px', fontSize: 13, color: '#344767', verticalAlign: 'middle' };

export default function PipelineStageManager() {
  const roleNum = parseInt(AuthService.getLoggedInRoleId(), 10);
  const isSuperAdmin = roleNum === 1;
  const defaultClientId = parseInt(AuthService.getLoggedInClientId(), 10) || 0;

  const [clientId, setClientId] = useState(defaultClientId);
  const [clients, setClients] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [msg, setMsg] = useState(null);
  const inputRef = useRef(null);

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 4000);
  };

  useEffect(() => {
    if (isSuperAdmin) {
      ClientService.getAllCLients()
        .then((data) => {
          const list = Array.isArray(data) ? data : data?.$values ?? data?.data ?? [];
          setClients(list);
          if ((!clientId || clientId <= 0) && list.length) {
            setClientId(list[0].id ?? list[0].Id);
          }
        })
        .catch(() => {});
    }
  }, [isSuperAdmin]);

  const load = useCallback(async () => {
    if (!clientId) {
      setStages([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await LeadStatusService.getPipelineStagesByClientId(clientId);
      setStages(Array.isArray(data) ? data : []);
    } catch {
      showMsg('Failed to load pipeline stages.', 'error');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (showForm && inputRef.current) inputRef.current.focus();
  }, [showForm]);

  const globalCount = stages.filter((s) => s.clientId == null).length;
  const customCount = stages.length - globalCount;

  const openCreate = () => {
    setEditingId(null);
    setForm(defaultForm);
    setShowForm(true);
  };

  const openEdit = (stage) => {
    setEditingId(stage.id);
    setForm({
      leadStatusName: stage.leadStatusName,
      color: stage.color || '#5e72e4',
      sortOrder: stage.sortOrder || 0,
    });
    setShowForm(true);
  };

  const cancel = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!form.leadStatusName.trim()) {
      showMsg('Stage name is required.', 'error');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await LeadStatusService.updatePipelineStage(editingId, { ...form });
        showMsg('Stage updated.');
      } else {
        await LeadStatusService.createPipelineStage({ ...form, clientId });
        showMsg('Custom stage created.');
      }
      setShowForm(false);
      await load();
    } catch (e) {
      showMsg(apiErrorMessage(e, 'Failed to save stage.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this custom stage? Global stages cannot be deleted.')) return;
    setDeletingId(id);
    try {
      await LeadStatusService.deletePipelineStage(id);
      showMsg('Stage deleted.');
      await load();
    } catch (e) {
      showMsg(apiErrorMessage(e, 'Cannot delete this stage.'), 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const stat = (label, value, color = '#5e72e4', icon = 'fa-layer-group') => (
    <div style={{
      padding: '14px 16px',
      borderRadius: 12,
      background: `linear-gradient(135deg, ${color}12, ${color}06)`,
      border: `1px solid ${color}22`,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      minWidth: 0,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, background: `${color}18`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', color, flexShrink: 0,
      }}>
        <i className={`fas ${icon}`} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, color: '#8898aa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#344767', lineHeight: 1.2 }}>{value ?? '—'}</div>
      </div>
    </div>
  );

  return (
    <div style={pageBg}>
      <style>{`
        .ps-inp:focus { border-color: #5e72e4 !important; box-shadow: 0 0 0 3px rgba(94,114,228,0.12); outline: none; }
        .ps-save:hover { opacity: 0.9; transform: translateY(-1px); }
        .ps-row:hover { background: #fafbfe; }
        .ps-table-wrap { overflow: auto; max-height: calc(100vh - 380px); }
        .ps-table-wrap thead th { position: sticky; top: 0; z-index: 2; background: #f6f9fc; box-shadow: 0 1px 0 #e9ecef; }
        .ps-color-swatch { transition: transform 0.12s ease; }
        .ps-color-swatch:hover { transform: scale(1.1); }
        .ps-color-swatch.active { outline: 2px solid #344767; outline-offset: 2px; }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 240 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 14,
            background: 'linear-gradient(135deg, #5e72e4 0%, #825ee4 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 20, boxShadow: '0 4px 14px rgba(94,114,228,0.35)', flexShrink: 0,
          }}>
            <i className="fas fa-layer-group" />
          </div>
          <div>
            <h4 style={{ margin: 0, fontWeight: 800, color: '#344767', fontSize: 22 }}>Pipeline Stages</h4>
            <p style={{ margin: '2px 0 0', color: '#8898aa', fontSize: 13 }}>
              Configure global and custom stages for your Kanban pipeline
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
          {isSuperAdmin && (
            <>
              <span style={{ fontWeight: 700, fontSize: 13, color: '#344767' }}>
                <i className="fas fa-building" style={{ marginRight: 6, color: '#5e72e4' }} />
                Agency
              </span>
              <select
                value={clientId}
                onChange={(e) => setClientId(parseInt(e.target.value, 10))}
                className="ps-inp"
                style={{ ...inp, width: 'auto', minWidth: 200, padding: '8px 12px' }}
              >
                {clients.map((c) => (
                  <option key={c.id ?? c.Id} value={c.id ?? c.Id}>
                    {c.agencyName ?? c.AgencyName}
                  </option>
                ))}
              </select>
            </>
          )}

          <button
            type="button"
            onClick={openCreate}
            disabled={!clientId}
            style={{
              padding: '10px 18px', borderRadius: 10, border: 'none',
              background: 'linear-gradient(135deg,#5e72e4,#825ee4)',
              color: '#fff', fontWeight: 700, cursor: clientId ? 'pointer' : 'not-allowed',
              fontSize: 13, opacity: clientId ? 1 : 0.6,
              display: 'inline-flex', alignItems: 'center', gap: 8,
              boxShadow: '0 4px 12px rgba(94,114,228,0.35)',
            }}
          >
            <i className="fas fa-plus" />
            Add custom stage
          </button>
        </div>
      </div>

      {msg && (
        <div style={{
          padding: '10px 14px', borderRadius: 10, marginBottom: 14,
          background: msg.type === 'error' ? '#fff5f5' : '#f0fff4',
          color: msg.type === 'error' ? '#c53030' : '#276749',
          fontWeight: 600, fontSize: 13,
          border: `1px solid ${msg.type === 'error' ? '#fed7d7' : '#c6f6d5'}`,
        }}>
          <i className={`fas ${msg.type === 'error' ? 'fa-exclamation-circle' : 'fa-check-circle'}`} style={{ marginRight: 8 }} />
          {msg.text}
        </div>
      )}

      <div style={card}>
        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
          {stat('Total stages', loading ? '…' : stages.length, '#5e72e4', 'fa-layer-group')}
          {stat('Global', loading ? '…' : globalCount, '#2dce89', 'fa-globe')}
          {stat('Custom', loading ? '…' : customCount, '#11cdef', 'fa-pencil-alt')}
        </div>

        {/* Add / Edit form */}
        {showForm && (
          <div style={{
            background: '#f8f9fc',
            borderRadius: 12,
            border: '1px solid #e9ecef',
            padding: '16px 18px',
            marginBottom: 16,
          }}>
            <p style={{ fontWeight: 700, color: '#344767', fontSize: 14, margin: '0 0 14px' }}>
              <i className={`fas ${editingId ? 'fa-pencil-alt' : 'fa-plus-circle'}`} style={{ marginRight: 8, color: '#5e72e4' }} />
              {editingId ? 'Edit stage' : 'Create custom stage'}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, alignItems: 'end' }}>
              <div style={{ gridColumn: 'span 2', minWidth: 200 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#8898aa', marginBottom: 4, textTransform: 'uppercase' }}>
                  Stage name *
                </label>
                <input
                  ref={inputRef}
                  type="text"
                  className="ps-inp"
                  value={form.leadStatusName}
                  onChange={(e) => setForm((f) => ({ ...f, leadStatusName: e.target.value }))}
                  placeholder="e.g. Property visit scheduled"
                  style={{ ...inp, padding: '8px 12px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#8898aa', marginBottom: 4, textTransform: 'uppercase' }}>
                  Sort order
                </label>
                <input
                  type="number"
                  className="ps-inp"
                  min={0}
                  value={form.sortOrder}
                  onChange={(e) => setForm((f) => ({ ...f, sortOrder: parseInt(e.target.value, 10) || 0 }))}
                  style={{ ...inp, maxWidth: 100, padding: '8px 12px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#8898aa', marginBottom: 6, textTransform: 'uppercase' }}>
                  Color
                </label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {PRESET_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`ps-color-swatch${form.color === c ? ' active' : ''}`}
                      onClick={() => setForm((f) => ({ ...f, color: c }))}
                      style={{
                        width: 24, height: 24, borderRadius: 6, background: c, border: 'none', cursor: 'pointer',
                      }}
                      title={c}
                    />
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={cancel}
                  style={{
                    padding: '8px 16px', borderRadius: 8, border: '1px solid #e9ecef',
                    background: '#fff', fontSize: 12, fontWeight: 600, color: '#8392ab', cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="ps-save"
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    padding: '8px 18px', borderRadius: 8, border: 'none',
                    background: saving ? '#adb5bd' : 'linear-gradient(135deg,#5e72e4,#825ee4)',
                    color: '#fff', fontWeight: 700, fontSize: 12,
                    cursor: saving ? 'wait' : 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    transition: 'all 0.15s ease',
                  }}
                >
                  <i className={saving ? 'fas fa-spinner fa-spin' : 'fas fa-save'} />
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #f0f2f5' }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#344767' }}>
            <i className="fas fa-stream" style={{ marginRight: 8, color: '#5e72e4' }} />
            Stage list
          </span>
          {loading && (
            <span style={{ color: '#8392ab', fontSize: 13 }}>
              <i className="fas fa-spinner fa-spin" /> Loading…
            </span>
          )}
        </div>

        {!loading && !clientId && (
          <div style={{ textAlign: 'center', padding: '32px 16px', color: '#8898aa' }}>
            <i className="fas fa-building" style={{ fontSize: 32, marginBottom: 12, opacity: 0.4 }} />
            <p style={{ margin: 0 }}>Select an agency above to load pipeline stages.</p>
          </div>
        )}

        {!loading && clientId && stages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 16px', color: '#8898aa' }}>
            <i className="fas fa-layer-group" style={{ fontSize: 32, marginBottom: 12, opacity: 0.4 }} />
            <p style={{ margin: 0 }}>No stages found for this workspace.</p>
          </div>
        )}

        {!loading && stages.length > 0 && (
          <div className="ps-table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
              <thead>
                <tr style={{ textAlign: 'left' }}>
                  <th style={{ ...th, width: 48 }} />
                  <th style={th}>Stage</th>
                  <th style={{ ...th, width: 80 }}>Order</th>
                  <th style={{ ...th, width: 90 }}>Type</th>
                  <th style={{ ...th, width: 100 }} />
                </tr>
              </thead>
              <tbody>
                {stages.map((stage) => {
                  const isGlobal = stage.clientId == null;
                  const color = stage.color || '#8392ab';
                  const isDeleting = deletingId === stage.id;
                  return (
                    <tr key={stage.id} className="ps-row" style={{ borderBottom: '1px solid #f0f2f5' }}>
                      <td style={td}>
                        <div style={{
                          width: 32, height: 32, borderRadius: 8,
                          background: `${color}20`, border: `2px solid ${color}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <i className="fas fa-circle" style={{ color, fontSize: 8 }} />
                        </div>
                      </td>
                      <td style={td}>
                        <div style={{ fontWeight: 700, color: '#344767', fontSize: 13 }}>
                          {getStatusDisplayName(stage.leadStatusName)}
                        </div>
                        {isGlobal && (
                          <div style={{ fontSize: 11, color: '#adb5bd', marginTop: 2 }}>
                            <i className="fas fa-lock" style={{ marginRight: 4, fontSize: 10 }} />
                            System stage — read only
                          </div>
                        )}
                      </td>
                      <td style={td}>
                        <span style={{
                          display: 'inline-block', padding: '3px 10px', borderRadius: 6,
                          background: '#f6f9fc', fontWeight: 700, fontSize: 12, color: '#525f7f',
                        }}>
                          {stage.sortOrder || 0}
                        </span>
                      </td>
                      <td style={td}>
                        <span style={{
                          padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                          background: isGlobal ? '#2dce8918' : '#5e72e418',
                          color: isGlobal ? '#2dce89' : '#5e72e4',
                          border: `1px solid ${isGlobal ? '#2dce8933' : '#5e72e433'}`,
                        }}>
                          {isGlobal ? 'Global' : 'Custom'}
                        </span>
                      </td>
                      <td style={td}>
                        {!isGlobal ? (
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              onClick={() => openEdit(stage)}
                              title="Edit"
                              style={{
                                padding: '5px 12px', borderRadius: 8, border: '1px solid #5e72e4',
                                background: '#5e72e414', color: '#5e72e4', fontWeight: 700,
                                fontSize: 11, cursor: 'pointer',
                              }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(stage.id)}
                              disabled={isDeleting}
                              title="Delete"
                              style={{
                                padding: '5px 12px', borderRadius: 8, border: 'none',
                                background: isDeleting ? '#adb5bd' : '#f5365c',
                                color: '#fff', fontWeight: 700, fontSize: 11,
                                cursor: isDeleting ? 'wait' : 'pointer',
                              }}
                            >
                              {isDeleting ? '…' : 'Delete'}
                            </button>
                          </div>
                        ) : (
                          <span style={{ fontSize: 11, color: '#adb5bd', float: 'right' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <p style={{ margin: '14px 0 0', fontSize: 12, color: '#adb5bd' }}>
          Global stages are shared across all workspaces. Custom stages apply only to the selected agency and appear in Kanban &amp; assigned leads.
        </p>
      </div>
    </div>
  );
}
